import { sumBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StackedBarData } from '../../../../CommonComponents/OPL/charts/StackedBar/StackedBar';
import { useTheme } from '../../../../theme/theme';
import { AsyncComponentHookResult } from '../../../../Types/ComponentHookResult';
import {
  AssetBucketingResultsDTO,
  AssetCategoryDTO,
  AssetDTO,
  BucketItem,
  HoldingDTO,
  useAssetBucketingResultsQuery,
} from './api/useAssetBucketingResultsQuery';
import { useAssetBucketingColors } from './hooks/useAssetBucketingColors';
import { getBucketChartSizesDict } from './utils/getBucketChartSizesDict';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

const MAX_ASSETS_COUNT = 4;

export interface ItemData
  extends Pick<AssetCategoryDTO, 'transactions' | 'networthValue' | 'displayValue'> {
  id: string;
  name: string;
  y: number;
  color?: string;
  isOther?: boolean;
  isUnassigned?: boolean;
  holdings: HoldingDTO[];
  isHoldingsBucketInheritedFromAsset?: boolean | null;
  excludedFromNetworth?: boolean;
}

export interface BucketData {
  id?: string;
  title: string;
  description: string;
  data: (ItemData & { assets?: AssetDTO[] })[];
  slicedData: ItemData[];
  total: number;
  chartSize: number;
  mainColor: string;
  chartTotal: number;
}

interface BuildBucketDataParams {
  title: string;
  description: string;
  items: BucketItem[];
  total: number;
  chartSizesDict: Record<string, number>;
  colors: string[];
  mainColor: string;
  otherAssetColor: string;
  showValue?: boolean;
  chartTotal: number;
}

export const buildBucketData = ({
  title,
  description,
  items,
  total,
  chartSizesDict,
  colors,
  mainColor,
  otherAssetColor,
  chartTotal,
}: BuildBucketDataParams): BucketData => {
  const parsedData = items
    .sort((a, b) => b.transactions.length - a.transactions.length || b.value - a.value)
    .map(
      (
        {
          name,
          description: itemDescription,
          transactions,
          assets,
          holdings,
          networthValue,
          displayValue,
          isHoldingsBucketInheritedFromAsset,
          chartValue,
        },
        index,
      ) => ({
        id: name,
        name: `${name}${itemDescription ? ` • ${itemDescription}` : ''}`,
        y: chartValue,
        color: colors[index % colors.length],
        transactions,
        assets,
        holdings,
        networthValue,
        displayValue,
        isHoldingsBucketInheritedFromAsset,
      }),
    );

  const bucketData: BucketData = {
    title,
    description,
    data: parsedData,
    slicedData: parsedData.slice(
      0,
      Math.max(
        MAX_ASSETS_COUNT,
        parsedData.filter(({ transactions }) => transactions.length > 0).length,
      ),
    ),
    total,
    mainColor,
    chartSize: chartSizesDict[title],
    chartTotal,
  };

  const otherItemsCount = parsedData.length - bucketData.slicedData.length;

  if (otherItemsCount > 0) {
    const value = total - bucketData.slicedData.reduce((acc, { y }) => acc + y, 0);

    const displayValue =
      total -
      bucketData.slicedData.reduce((acc, slicedItem) => {
        if (slicedItem.displayValue === null) {
          return acc;
        }

        return acc + slicedItem.displayValue;
      }, 0);

    const hasHideValue = parsedData.some(slicedItem => slicedItem.displayValue === null);

    bucketData.slicedData.push({
      id: `${otherItemsCount} Other items`,
      name: `${otherItemsCount} Other items`,
      y: value,
      color: otherAssetColor,
      isOther: true,
      transactions: [],
      holdings: [],
      networthValue: value,
      displayValue: displayValue === 0 && hasHideValue ? null : displayValue,
    });
  }

  return bucketData;
};

export interface UseAssetBucketingData
  extends Pick<AssetBucketingResultsDTO, 'year' | 'yearOptions' | 'netWorth'> {
  buckets: BucketData[];
  stackedBarData: StackedBarData;
  showFullBreakDown: boolean;
  description: string;
  title: string;
  isDefaultAssetBucketingLayout: boolean;
  allowAssetBucketingCustomization: boolean;
  hasExcludedFromNetworth: boolean;
}

interface UseAssetBucketingActions {
  handleAddGoal(): void;
  handleNetworthClick?: () => void;
}

interface UseAssetBucketingResultsParams {
  year?: number;
}

export const useAssetBucketingResults = ({
  year,
}: UseAssetBucketingResultsParams = {}): AsyncComponentHookResult<
  UseAssetBucketingData,
  UseAssetBucketingActions
> => {
  const { Id: clientId } = useSelector(selectCurrentClient);

  const assetYear = year || new Date().getFullYear(); // current year

  const queryResult = useAssetBucketingResultsQuery(assetYear);

  const theme = useTheme();

  const bucketColors = useAssetBucketingColors();

  useEffect(() => {
    if (
      queryResult.isSuccess &&
      !queryResult.data.yearOptions.find(opt => opt.value === queryResult.data.year)
    ) {
      ADV.util.Router.go2('AssetBucketingResults', {
        clientId,
        year: new Date().getFullYear(),
      });
    }
  });

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const { buckets, yearOptions, ...rest } = queryResult.data;

    const chartSizesDict = getBucketChartSizesDict(
      buckets.map(({ title, items }) => ({
        title,
        items: items.map(({ value }) => ({ value })),
      })),
    );

    const hasExcludedFromNetworth = buckets.some(bucket => bucket.hasExcludedFromNetworth);

    const bucketsData = buckets.map(({ title, items, description }, index) => {
      const colors = bucketColors[index];

      const mainColor = colors[0];

      const total = items.reduce((acc, cur) => acc + cur.value, 0);
      const chartTotal = items.reduce((acc, cur) => acc + cur.chartValue, 0);

      return buildBucketData({
        title,
        description,
        items,
        total,
        chartSizesDict,
        colors,
        mainColor,
        otherAssetColor: theme.colors.neutral[400],
        chartTotal,
      });
    });

    const stackedBarData: StackedBarData = bucketsData.map(({ title, total, mainColor }) => ({
      id: title.trim(),
      label: title,
      value: total,
      color: mainColor,
    }));

    const allTotal = sumBy(bucketsData, 'total');

    const showFullBreakDown = allTotal !== 0;

    return {
      buckets: bucketsData,
      stackedBarData,
      showFullBreakDown,
      yearOptions,
      hasExcludedFromNetworth,
      ...rest,
    };
  }, [bucketColors, queryResult.data, theme.colors.neutral]);

  const handleAddGoal = () => {
    ADV.util.Router.go2('ClientEditPlan', {
      clientId,
    });
  };

  const handleNetworthClick =
    data?.year === data?.yearOptions[0].value
      ? () => {
          ADV.util.Router.go2('NetWorth', {
            clientId,
            pivot: 0,
          });
        }
      : undefined;

  return {
    ...queryResult,
    data,
    actions: {
      handleAddGoal,
      handleNetworthClick,
    },
  };
};
